import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { AlertTriangle } from 'lucide-react';
import ActiveStrip from './ActiveStrip.jsx';
import FeaturedCard from './FeaturedCard.jsx';
import CuratedGrid from './CuratedGrid.jsx';
import MarketplacePromoBanners from './MarketplacePromoBanners.jsx';
import MarketplaceBrowseAll from './MarketplaceBrowseAll.jsx';
import { useMarketplaceCurated } from '../hooks/marketplace';

/**
 * Marketplace tab content (CT-4163…CT-4169). This story lands the Active Strip,
 * Featured Card and curated grids (CT-4164). Promo banners (CT-4165), Browse
 * All (CT-4166), filters (CT-4168) and empty state (CT-4169) mount here as they land.
 */
const MarketplaceTab = () => {
  const { t } = useTranslation('admin-studio');
  const navigate = useNavigate();
  const { activeSummary, featured, curated, isLoading, error } = useMarketplaceCurated();

  // Stable handlers so the memoized ActiveStrip / FeaturedCard are not re-rendered
  // on every parent render. Declared before the early returns (Rules of Hooks).
  const openProvider = useCallback((slug) => navigate(slug), [navigate]);
  const handleViewAll = useCallback(() => {
    // TODO(CT-4164): scroll to the curated catalog once it lands (no target yet).
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ActiveStrip summary={activeSummary} onOpenProvider={openProvider} onViewAll={handleViewAll} />
      <FeaturedCard view={featured} onOpen={openProvider} />
      <CuratedGrid
        titleKey="integrations.marketplace.curated.popular"
        items={curated.popular}
        onOpen={openProvider}
      />
      <CuratedGrid
        titleKey="integrations.marketplace.curated.newFromPartners"
        items={curated.newFromPartners}
        onOpen={openProvider}
      />
      <MarketplacePromoBanners />
      <MarketplaceBrowseAll />
    </div>
  );
};

export default MarketplaceTab;
